import { type MouseEvent, useEffect, useRef } from 'react';
import type { SingleValue } from 'react-select';

import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { usePrimaryEntityKey } from '../../DataProvider/subsidiaries/use-primary-company';
import type {
  PrimaryEntity,
  PrimaryEntityOption,
} from '../../PageConfigProvider/types/chart';
import { usePageState } from '../../PageState/PageStateProvider';
import { useChart } from '../ChartProvider';
import { StyledPlotSelect } from '../StyledPlotSelect';
import styles from './chart-switcher.module.css';

export const PrimaryEntitySwitcher = ({
  isSingleEntityChart,
  primaryFilterId,
}: {
  isSingleEntityChart: boolean;
  primaryFilterId: PrimaryFilterId;
}) => {
  const { item } = useChart();
  const { pageState, dispatch: pageDispatch } = usePageState();
  const entityKey = usePrimaryEntityKey(item);

  const { primaryFilter: primaryFilterState } =
    usePrimaryFilterState(primaryFilterId);

  const { isSingleEntity } = item?.plot ?? {};

  const primaryEntityOptions: PrimaryEntityOption[] =
    primaryFilterState?.primarySelections?.map((selection) => ({
      label: selection?.name,
      value: {
        id: selection?.id.split('_').pop() || selection?.id,
        name: selection?.name,
        levelId: selection.levelId,
      },
    })) || [];

  const optionValues = primaryEntityOptions.map((o) => o.value);

  const selectedPrimaryEntityOption = (() => {
    const selectedEntity = entityKey
      ? pageState.primaryEntities?.[entityKey]
      : undefined;
    if (!selectedEntity) return null;

    return primaryEntityOptions.find(
      (option) =>
        option.value.levelId === selectedEntity.levelId &&
        option.value.id === selectedEntity.id
    );
  })();

  const showSwitcher =
    isSingleEntity || (isSingleEntityChart && primaryEntityOptions.length > 1);

  /** When going from a single primary entity selection to multiple primary selections,
   * use the last single selection as the default switcher value */
  const lastSingleSelection = useRef<PrimaryEntity | null>(null);
  if (primaryEntityOptions.length === 1) {
    lastSingleSelection.current = primaryEntityOptions?.[0]?.value ?? null;
  }

  const lastIsValid = lastSingleSelection.current
    ? optionValues.some(
        (v) =>
          v.levelId === lastSingleSelection.current?.levelId &&
          v.id === lastSingleSelection.current?.id
      )
    : false;

  const defaultPrimaryEntity = lastIsValid
    ? lastSingleSelection.current
    : (optionValues?.[0] ?? null);

  useEffect(() => {
    if (showSwitcher && !selectedPrimaryEntityOption && defaultPrimaryEntity) {
      pageDispatch({
        type: 'SET_PRIMARY_ENTITY',
        payload: {
          plotId: item?.id,
          primaryEntity: defaultPrimaryEntity,
          entityKey,
        },
      });
    }

    if (!showSwitcher && pageState.primaryEntities?.[item?.id]) {
      pageDispatch({
        type: 'CLEAR_PRIMARY_ENTITIES',
        payload: { plotIds: [item?.id] },
      });
    }
  }, [
    showSwitcher,
    item?.id,
    entityKey,
    pageDispatch,
    defaultPrimaryEntity,
    selectedPrimaryEntityOption,
    pageState.primaryEntities,
  ]);

  const handleSelectedPrimaryFilterChange = (
    entity: SingleValue<PrimaryEntityOption>
  ) => {
    if (!entity) return;

    pageDispatch({
      type: 'SET_PRIMARY_ENTITY',
      payload: {
        plotId: item?.id,
        primaryEntity: entity.value,
        fromChartSwitcher: true,
        entityKey,
      },
    });
  };

  const hideSingleEntitySentimentSwitcher =
    item?.plot?.chartType === 'sentiment_review' &&
    primaryEntityOptions.length === 1;

  if (
    !showSwitcher ||
    primaryEntityOptions.length <= 1 ||
    hideSingleEntitySentimentSwitcher
  ) {
    return null;
  }

  return (
    <div
      className={styles.switcherBox}
      onClick={(e: MouseEvent) => e.stopPropagation()}
    >
      <StyledPlotSelect<PrimaryEntityOption>
        value={selectedPrimaryEntityOption}
        onChange={handleSelectedPrimaryFilterChange}
        options={primaryEntityOptions}
        height="24px"
        classNamePrefix="primary-switch"
        isOptionSelected={(option) => {
          const optionValue = option.value;
          const selectedOptionValue = selectedPrimaryEntityOption?.value;
          return (
            optionValue.levelId === selectedOptionValue?.levelId &&
            optionValue.id === selectedOptionValue?.id
          );
        }}
      />
    </div>
  );
};
